import { useState, useEffect, useRef } from 'react';
import { useApp } from '../App';
import api from '../api';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

export default function Receipts() {
  const { user } = useApp();
  const { t } = useI18n();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    loadReceipts();
  }, []);

  async function loadReceipts() {
    try {
      const { data } = await api.get('/receipts/my');
      setReceipts(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: t('receipts.only_images') });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Файл слишком большой (макс. 10MB)' });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await api.post('/receipts/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      setMessage({ type: 'success', text: '✅ Чек загружен и отправлен на проверку!' });
      loadReceipts();
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || t('receipts.upload_error') });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  function formatDate(isoString) {
    if (!isoString) return '';
    const d = new Date(isoString);
    return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  }

  const statusLabels = {
    pending: '⏳ На проверке',
    approved: '✅ Одобрен',
    rejected: '❌ Отклонён',
  };

  const statusColors = {
    pending: '#f59e0b',
    approved: '#10b981',
    rejected: '#ef4444',
  };

  if (loading) return <div className="page"><BackButton /><div className="loader">{t('common.loading')}</div></div>;

  return (
    <div className="page">
      <BackButton />
      <h1>{t('receipts.title')}</h1>

      {message && (
        <div style={{
          padding: 12,
          borderRadius: 8,
          marginBottom: 16,
          background: message.type === 'error' ? 'rgba(220,38,38,0.2)' : 'rgba(16,185,129,0.2)',
        }}>
          {message.text}
        </div>
      )}

      {/* Upload Button */}
      <div
        onClick={() => !uploading && fileRef.current?.click()}
        style={{
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          borderRadius: 12,
          padding: 24,
          textAlign: 'center',
          cursor: uploading ? 'wait' : 'pointer',
          marginBottom: 24,
        }}
      >
        <div style={{ fontSize: 48, marginBottom: 8 }}>📸</div>
        <div style={{ fontWeight: 'bold', fontSize: 18, marginBottom: 4 }}>
          {uploading ? '⏳ Загрузка...' : 'Загрузить чек или фактуру'}
        </div>
        <div style={{ fontSize: 13, opacity: 0.8 }}>
          Сфотографируйте или выберите файл
        </div>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleUpload}
        style={{ display: 'none' }}
      />

      {/* Info */}
      <div style={{
        background: 'rgba(255,255,255,0.05)',
        borderRadius: 8,
        padding: 12,
        marginBottom: 16,
        fontSize: 13,
      }}>
        💡 После загрузки чек отправляется суперадмину на проверку. Если чек действителен — сумма будет добавлена к вашему балансу.
      </div>

      {/* Receipts List */}
      <h3>📋 Загруженные чеки</h3>

      {receipts.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: 32,
          background: 'rgba(255,255,255,0.05)',
          borderRadius: 12,
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🧾</div>
          <p style={{ opacity: 0.7 }}>{t('receipts.no_receipts')}</p>
        </div>
      ) : (
        <div className="list">
          {receipts.map(r => (
            <div key={r.id} style={{
              background: 'rgba(255,255,255,0.05)',
              borderRadius: 12,
              padding: 16,
              marginBottom: 12,
            }}>
              <div style={{ display: 'flex', gap: 12 }}>
                {r.image_url && (
                  <img
                    src={r.image_url}
                    alt="Receipt"
                    style={{
                      width: 60,
                      height: 60,
                      objectFit: 'cover',
                      borderRadius: 8,
                    }}
                  />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <div style={{ fontWeight: 'bold' }}>Чек #{r.id}</div>
                      {r.amount && (
                        <div style={{ fontSize: 18, fontWeight: 'bold', color: '#10b981' }}>
                          {r.amount} {r.currency || 'PLN'}
                        </div>
                      )}
                    </div>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: 4,
                      fontSize: 11,
                      background: statusColors[r.status] || '#6b7280',
                    }}>
                      {statusLabels[r.status] || r.status}
                    </span>
                  </div>
                  {r.admin_comment && (
                    <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                      💬 {r.admin_comment}
                    </div>
                  )}
                  <div style={{ fontSize: 11, opacity: 0.5, marginTop: 4 }}>
                    {formatDate(r.created_at)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
